import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAuth0Token } from '@/utils/auth0Token';
import { handleAuthError } from '@/utils/handleAuthError';

// Helper to check if user is authenticated via Auth0 cache
// This avoids importing useAuth0 hook in non-React code
const isUserAuthenticated = () => {
	if (typeof window === 'undefined') {
		return false;
	}
	try {
		const auth0CacheKeys = Object.keys(localStorage).filter(key => 
			key.includes('@@auth0spajs@@')
		);
		if (auth0CacheKeys.length === 0) {
			return false;
		}

		for (const key of auth0CacheKeys) {
			try {
				const cacheValue = localStorage.getItem(key);
				if (cacheValue) {
					const cacheData = JSON.parse(cacheValue);
					if (cacheData?.body?.isAuthenticated === true) {
						return true;
					}
					if (cacheData?.body?.access_token || cacheData?.body?.user) {
						return true;
					}
				}
			} catch (e) {
				continue;
			}
		}
		return false;
	} catch (error) {
		return false;
	}
};

/**
 * Handles 401/403 errors from baseQuery results
 * @param {Object} result - The result from the baseQuery
 * @param {Object} args - The original request args (for logging)
 */
const handleBaseQueryAuthError = (result, args) => {
	if (!result.error) return;
	
	const status = result.error.status;
	const errorData = result.error.data || result.error;
	const endpoint = args?.url || 'unknown';
	
	// Use centralized error handler (only handles 401/403)
	handleAuthError(status, errorData, endpoint);
};

/**
 * Creates a baseQuery with authentication headers and 401 error handling
 * Uses Auth0 SDK's getAccessTokenSilently() method (best practice)
 * @param {string} baseUrl - The base URL for the API
 * @returns {Function} A baseQuery function that can be used with RTK Query
 */
export const createBaseQueryWithAuth = (baseUrl) => {
		return async (args, api, extraOptions) => {
		const baseQuery = fetchBaseQuery({ 
			baseUrl: baseUrl,
			prepareHeaders: async (headers) => {
				const isAuthenticated = isUserAuthenticated();
				
				if (!isAuthenticated) {
					return headers; // Return headers without token
				}
				
				// Get token - tries localStorage first (immediate), then SDK method (handles refresh)
				const token = await getAuth0Token();
				
				if (token) {
					headers.set('authorization', `Bearer ${token}`);
				}
				
				return headers;
			},
		});
		
		const result = await baseQuery(args, api, extraOptions);
		
		// Log the result for debugging
		if (result.error) {
			console.error('❌ baseQuery: API call failed', {
				status: result.error.status,
				error: result.error.error,
				data: result.error.data,
				endpoint: args?.url || 'unknown',
				baseUrl: baseUrl,
				pathname: window.location.pathname,
				// Log the actual response if available
				responseText: result.error.data?.message || result.error.data?.error || JSON.stringify(result.error.data)
			});
		}
		
		// Handle 401 Unauthorized and 403 Forbidden errors
		handleBaseQueryAuthError(result, args);
		
		return result;
	};
};

/**
 * Wraps an existing baseQuery result with 401/403 error handling
 * Useful when you have a custom baseQuery (like biovizApi with CORS handling)
 * @param {Object} result - The result from the baseQuery
 * @param {Object} args - The original request args (for logging)
 * @returns {Object} The result (potentially modified)
 */
export const wrapBaseQueryResult = (result, args) => {
	handleBaseQueryAuthError(result, args);
	return result;
};

